import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CompanyInfoForm from '@/components/CompanyInfoForm';
import DataConnectionForm from '@/components/DataConnectionForm';
import Header from '@/components/Header';
import AuthForm from '@/components/AuthForm';
import LoadingState from '@/components/LoadingState';
import FormCompleted from '@/components/FormCompleted';
import { useFormDrafts } from '@/hooks/useFormDrafts';
import { CompanyData } from '@/types';
import { StandardizedConnection } from '@/types/connections';
import { saveCompanyAndConnections, saveFormDraft } from '@/utils/organization/saveOrganization';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';
import { useToast } from '@/hooks/use-toast';
import { Save } from 'lucide-react';
import { generateSubmissionIdentifier } from '@/utils/magic-link/helpers';
import { getTokenFromUrl, loadFormDataFromToken, removeTokenFromUrl } from '@/utils/formTokenManager';
import { sendMagicLinkEmail } from '@/utils/magic-link/sendMagicLinkEmail';
import { ExportNotificationsProvider } from '@/components/data-table/ExportNotificationsProvider';
import { exportMonitoringService } from "@/utils/organization/exportMonitoringService";
import { initializeExportCleanup } from "@/utils/organization/exportCleanup";
import { monitorExportHealth } from '@/utils/organization/exportCleanupService';

const Index = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract step from pathname directly since useParams might not work with our route setup
  const getStepFromPath = () => {
    const pathMatch = location.pathname.match(/\/step\/(\d+)/);
    return pathMatch ? parseInt(pathMatch[1]) : 1;
  };

  const [step, setStep] = useState(getStepFromPath());
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [formTouched, setFormTouched] = useState(false);
  const [isLoadingFromToken, setIsLoadingFromToken] = useState(false);
  const { toast } = useToast();

  const {
    companyData,
    setCompanyData,
    formCompleted,
    setFormCompleted,
    session,
    pendingSave,
    setPendingSave,
    isLoading,
    isVerifyingToken,
    saveDraft,
    tokenDetected,
    loadDraftById,
    disableEin
  } = useFormDrafts();

  useEffect(() => {
    const handleTokenLoad = async () => {
      const token = getTokenFromUrl();
      if (token && !isLoadingFromToken) {
        setIsLoadingFromToken(true);

        try {
          const formData = await loadFormDataFromToken(token);
          if (formData) {
            removeTokenFromUrl();

            const savedStep = formData.savedFormStep || sessionStorage.getItem('savedFormStep');
            if (savedStep) {
              navigate(`/step/${savedStep}`, { replace: true });
            }

            setTimeout(() => {
              const savedData = sessionStorage.getItem('figabl_company_data');
              if (savedData) {
                try {
                  const parsedData = JSON.parse(savedData);
                  if (!Array.isArray(parsedData.products)) {
                    parsedData.products = [];
                  }
                  setCompanyData(parsedData);
                } catch (error) {
                  console.error('Error parsing restored company data:', error);
                }
              }
            }, 100);
          }
        } catch (error) {
          console.error('Failed to load form data from token:', error);
          removeTokenFromUrl();
          toast({
            title: "Error",
            description: "Failed to restore your form data. Please try again.",
            variant: "destructive"
          });
        } finally {
          setIsLoadingFromToken(false);
        }
      }
    };

    handleTokenLoad();
  }, [toast, setCompanyData, navigate, isLoadingFromToken]);

  useEffect(() => {
    if (!companyData && !isLoading && !isVerifyingToken && !tokenDetected && !formCompleted && !isLoadingFromToken) {
      const savedData = sessionStorage.getItem('figabl_company_data');
      const mfaResetCompleted = sessionStorage.getItem('figabl_mfa_completed');
      const mfaResetUserEmail = sessionStorage.getItem('figabl_mfa_reset_user_email');

      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (!Array.isArray(parsedData.products)) {
            parsedData.products = [];
          }
          setCompanyData(parsedData);

          // If this is from MFA reset, navigate to the appropriate step
          if (mfaResetCompleted && mfaResetUserEmail) {
            const savedStep = sessionStorage.getItem('savedFormStep');
            if (savedStep && parseInt(savedStep) === 2) {
              navigate('/step/2', { replace: true });
            }
            // Clean up the MFA reset flags
            sessionStorage.removeItem('figabl_mfa_reset_user_email');
          }
        } catch (error) {
          console.error('Error parsing sessionStorage company data:', error);
        }
      }
    }
  }, [companyData, isLoading, isVerifyingToken, setCompanyData, tokenDetected, loadDraftById, isLoadingFromToken, navigate]);

  // Initialize export services when on step 2 (data connection form)
  useEffect(() => {
    if (step === 2) {
      // Initialize export cleanup and monitoring for data connection step
      initializeExportCleanup();
      exportMonitoringService.startAutomaticCleanup();

      // Start periodic health monitoring (skip in E2E tests)
      let healthMonitorInterval: NodeJS.Timeout;
      if (!navigator.webdriver) {
        healthMonitorInterval = setInterval(monitorExportHealth, 60000);
      }

      return () => {
        exportMonitoringService.stopAutomaticCleanup();
        if (healthMonitorInterval) {
          clearInterval(healthMonitorInterval);
        }
      };
    }
  }, [step]);

  useEffect(() => {
    const newStep = getStepFromPath();

    // Check if this is a first-time user who has completed MFA or MFA reset user
    const storedCompanyData = sessionStorage.getItem('figabl_company_data');
    const hasEmailFromStep1 = storedCompanyData ? JSON.parse(storedCompanyData)?.pocEmail : null;
    const mfaCompletedInSession = sessionStorage.getItem('figabl_mfa_completed');
    const mfaResetUserEmail = sessionStorage.getItem('figabl_mfa_reset_user_email');
    const isFirstTimeUserWithMfa = !companyData && hasEmailFromStep1 && mfaCompletedInSession;
    const isMfaResetUser = mfaCompletedInSession && mfaResetUserEmail;

    // Only redirect to step 1 if they're on step 2 without company data AND they're not a first-time user with completed MFA AND not an MFA reset user
    if (newStep === 2 && !storedCompanyData && !isFirstTimeUserWithMfa && !isMfaResetUser) {
      navigate('/step/1');
      return;
    }

    if (newStep !== step) {
      setStep(newStep);
    }
  }, [location.pathname, step, companyData, navigate]);

  useEffect(() => {
    const savedStep = sessionStorage.getItem('savedFormStep');
    const isConnectorRedirect = sessionStorage.getItem('connectorRedirect') === 'true';

    if (savedStep && isConnectorRedirect && !isLoadingFromToken) {
      const storedCompanyData = sessionStorage.getItem('figabl_company_data');
      const parsedCompanyData = storedCompanyData ? JSON.parse(storedCompanyData) : null;
      if (parsedCompanyData) setCompanyData(parsedCompanyData)
      navigate(`/step/${savedStep}`);
    }
  }, [navigate, isLoadingFromToken]);

  const handleCompanyDataSubmit = async (data: CompanyData, isFinalSubmit?: boolean) => {
    console.log("handleCompanysubmit")
    const dataToSave = {
      ...data,
      products: Array.isArray(data.products) ? data.products : []
    };

    // Enhanced organization_id preservation with better debugging
    let organizationId = companyData?.organization_id || data.organization_id;

    // Fallback: try to get from sessionStorage
    if (!organizationId) {
      const savedData = sessionStorage.getItem('figabl_company_data');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          organizationId = parsedData.organization_id;
        } catch (error) {
          console.error('Error parsing sessionStorage for organization_id:', error);
        }
      }
    }

    // Always set organization_id if we have one
    if (organizationId) {
      dataToSave.organization_id = organizationId;
      // Also update sessionStorage to ensure persistence during navigation
      sessionStorage.setItem('figabl_company_data', JSON.stringify({
        ...dataToSave,
        organization_id: organizationId
      }));
    }

    // Merge connections: preserve rich metadata from existing companyData connections
    if (Array.isArray(dataToSave.connections) && Array.isArray(companyData?.connections)) {
      const existingById = new Map(companyData.connections.map(c => [c.id, c]));
      dataToSave.connections = dataToSave.connections.map(conn => {
        const existing = existingById.get(conn.id);
        if (existing) {
          return {
            ...existing,
            ...conn,
            // Prefer existing rich fields if incoming is missing
            service: conn.service || existing.service,
            connectorId: conn.connectorId || existing.connectorId,
            connectionId: conn.connectionId || existing.connectionId,
          };
        }
        return conn;
      });
    }

    setCompanyData(dataToSave);
    setFormTouched(false);
    setPendingSave(dataToSave);

    sessionStorage.setItem('figabl_company_data', JSON.stringify(dataToSave));


    if (!isFinalSubmit) {
      try {
        if (session?.user) {
          console.log("handleCompanysubmit 2")
          const { success, data, error } = await saveFormDraft(dataToSave);
          if (success) {
            toast({
              title: "Draft saved",
              description: "Your form draft has been saved successfully."
            });

            if (data?.id) {
              dataToSave.organization_id = data.id;
              setCompanyData(dataToSave);
              sessionStorage.setItem('figabl_company_data', JSON.stringify({
                ...dataToSave,
              }));
            }

          } else {
            console.error("Error saving draft:", error);
            if (error == "Duplicate EIN") {
              toast({
                title: "Duplicate EIN",
                description: "This EIN is already registered in our system.",
                variant: "destructive"
              });
            }
            else {
              toast({
                title: "Error saving draft",
                description: error || "An unexpected error occurred while saving your draft.",
                variant: "destructive"
              });
            }
            return;
          }
        } else {
          console.log("handleCompanysubmit 3")
          if (dataToSave.pocEmail) {
            const email = dataToSave.pocEmail
            handleAuthSuccess({ user: { email } } as unknown as Session)
          }
        }
      } catch (err) {
        console.error("Error in draft saving process:", err);
        toast({
          title: "Error",
          description: "Failed to save your draft. Please try again.",
          variant: "destructive"
        });
      }
    }

    if (!isFinalSubmit) {
      navigate('/step/2');
    }
  };

  const handleFormComplete = async (linkedConnectors?: StandardizedConnection[]) => {
    if (!companyData) {
      toast({
        title: "Error",
        description: "Missing company data for submission.",
        variant: "destructive"
      });
      return;
    }

    try {
      const dataToSubmit = {
        ...companyData,
        products: Array.isArray(companyData?.products) ? companyData.products : [],
        connections: linkedConnectors || []
      };

      toast({
        title: "Processing",
        description: "Preparing your submission...",
      });

      const { success, error, organizationId } = await saveCompanyAndConnections(dataToSubmit, linkedConnectors || [], false);
      if (!success) {
        if (error && error.includes('EIN already exists')) {
          toast({
            title: "Duplicate EIN",
            description: "This EIN is already registered in our system.",
            variant: "destructive"
          });
          navigate('/step/1');
          return;
        }

        throw new Error(error || 'Failed to save submission');
      }

      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('figabl_')) {
          sessionStorage.removeItem(key);
        }
      });

      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('connection_') || key === 'selectedConnectorData' ||
          key === 'connectorRedirect' || key === 'selectedConnectorId') {
          sessionStorage.removeItem(key);
        }
      });

      sessionStorage.removeItem('figabl_company_data');

      const submissionIdentifier = generateSubmissionIdentifier();

      const redirectTo = `${window.location.origin}/auth-callback?submission_identifier=${submissionIdentifier}`;

      // Send magic link with the organization ID (org ID is in companyData.organization_id)
      const { success: emailSuccess, error: emailError } = await sendMagicLinkEmail(
        companyData.pocEmail,
        "",
        { ...companyData, organization_id: organizationId },
        false
      );

      if (!emailSuccess) {
        console.error('Failed to send magic link email:', emailError);
        // Fallback to OTP
        await supabase.auth.signInWithOtp({
          email: companyData.pocEmail,
          options: {
            emailRedirectTo: redirectTo,
          }
        });
      }

      toast({
        title: "Magic link sent",
        description: "Check your email for the login link. You can open it on any device."
      });

      setFormCompleted(true);

      toast({
        title: "Success",
        description: "Your submission has been saved successfully."
      });

      // Immediately redirect to confirmation page without signing out
      window.location.replace('/confirmation');
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Failed to save your submission. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleFormTouch = (data: CompanyData) => {
    const updatedData = {
      ...data,
      products: Array.isArray(data.products) ? data.products : []
    };

    // Ensure organization_id is preserved
    let organizationId = companyData?.organization_id || data.organization_id;

    // Fallback: try to get from sessionStorage
    if (!organizationId) {
      const savedData = sessionStorage.getItem('figabl_company_data');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          organizationId = parsedData.organization_id;
        } catch (error) {
          console.error('Error parsing sessionStorage for organization_id in handleFormTouch:', error);
        }
      }
    }

    if (organizationId) {
      updatedData.organization_id = organizationId;
    }

    setFormTouched(true);
    setCompanyData(updatedData);
    sessionStorage.setItem('figabl_company_data', JSON.stringify(updatedData));
  };

  const handleSaveProgress = async () => {
    let dataToSave
    if (companyData) {
      dataToSave = {
        ...companyData,
        products: Array.isArray(companyData.products) ? companyData.products : []
      };
    } else {
      const savedData = sessionStorage.getItem('figabl_company_data')
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (!Array.isArray(parsedData.products)) {
            parsedData.products = [];
          }
          dataToSave = parsedData
        } catch (error) {
          console.error('Error parsing restored company data:', error);
        }
      }
    }

    setPendingSave(dataToSave);
    setShowSavePrompt(true);
  };

  const handleAuthSuccess = async (newSession: Session) => {
    setShowSavePrompt(false);

    const submissionIdentifier = generateSubmissionIdentifier();
    await saveDraft(companyData, newSession, submissionIdentifier);
  };

  if (isLoading || isVerifyingToken || isLoadingFromToken) {
    let message = "Loading your data...";
    if (isVerifyingToken) {
      message = "Verifying your login and loading your data...";
    } else if (isLoadingFromToken) {
      message = "Restoring your form data...";
    }

    return <LoadingState message={message} />;
  }

  if (step < 1 || step > 2 || isNaN(step)) {
    navigate('/step/1', { replace: true });
    return null;
  }

  if (showSavePrompt) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
              <h1 className="text-2xl font-bold text-brand-basic">Save Your Progress</h1>
              <p className="text-gray-600 mt-2">Generate a magic link to save your progress and continue later</p>
            </div>
            <AuthForm
              onAuthSuccess={handleAuthSuccess}
              onCancel={() => setShowSavePrompt(false)}
              initialEmail={companyData?.pocEmail || ''}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <ExportNotificationsProvider enabled={step === 2}>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          {formCompleted ? (
            <FormCompleted />
          ) : (
            <>
              <div className="mb-8">
                <div className="flex items-center justify-center mb-6">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-brand-primary text-white' : 'bg-gray-200 text-gray-500'}`}>
                      1
                    </div>
                    <div className={`h-1 w-24 ${step >= 2 ? 'bg-brand-primary' : 'bg-gray-200'}`}></div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-brand-primary text-white' : 'bg-gray-200 text-gray-500'}`}>
                      2
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <div className="text-center mb-4">
                    <h1 className="text-2xl font-bold text-brand-basic">
                      {step === 1 ? 'Company Information' : 'Connect Data Source'}
                    </h1>
                    <p className="text-gray-600 mt-2">
                      {step === 1 ? 'Provide your company details and product information' : 'Connect your data sources securely'}
                    </p>
                  </div>

                  {(formTouched || companyData) && (
                    <div className="fixed bottom-8 right-8 z-50">
                      <button
                        onClick={handleSaveProgress}
                        className="flex items-center gap-2 px-4 py-2 border border-brand-primary text-brand-primary rounded-full shadow-lg hover:bg-gray-50 transition-all"
                        title="Save your progress and continue later"
                      >
                        <Save size={16} /> Save Progress
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {step === 1 && (
                <CompanyInfoForm
                  onSubmit={handleCompanyDataSubmit}
                  initialData={companyData}
                  disableEin={disableEin}
                  onFieldChange={handleFormTouch}
                />
              )}
              {step === 2 && companyData && (
                <DataConnectionForm
                  companyData={companyData}
                  onBack={() => {
                    sessionStorage.removeItem('savedFormStep')
                    navigate('/step/1')
                  }}
                  onComplete={handleFormComplete}
                  isFinalStep={true}
                  onCompanyDataUpdate={(data) => {
                    setCompanyData(data);
                    sessionStorage.setItem('figabl_company_data', JSON.stringify(data));
                  }}
                />
              )}
            </>
          )}
        </main>

        <footer className="bg-white py-4 border-t">
          <div className="container mx-auto px-4">
            <p className="text-center text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Figabl. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </ExportNotificationsProvider>
  );
};

export default Index;
